import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../../context/FittingContext";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { submitFitting } from "../../api/ScoringApi";
import Alert from "@mui/material/Alert";
import { v4 as uuidv4 } from "uuid";

const EndSession = () => {
	const navigate = useNavigate();
	const { clubState, setSelectedShaft, setSelectedHead, setSelectedBall } = useContext(Context);
	const [submitError, setSubmitError] = useState(false);
	const [showSuccessToast, setShowSuccessToast] = useState(false);
	const clubType = localStorage.getItem("clubType") || "Drivers";
	const showBallSelection = clubType === "Drivers" || clubType === "Irons";
	return (
		<Grid container>
			{submitError ? (
				<Grid item xs={12}>
					<Alert severity="error">
						<Typography>
							Could not submit fitting session information. Please contact an
							administrator. To end the session without submitting, please click
							here
							<Button
								variant="contained"
								color="error"
								sx={{ marginLeft: 2 }}
								onClick={() => {
									localStorage.removeItem("sessionId");
									localStorage.removeItem("fittingStartedSessionId");
									localStorage.removeItem("fitterName");
									localStorage.removeItem("locationId");
									localStorage.removeItem("rightHanded");
									localStorage.removeItem("gender");
									localStorage.removeItem("clubType");
									localStorage.removeItem("handicap");
									localStorage.removeItem("sessionData");
									localStorage.removeItem("ballFittingCompleted");
									window.location = window.location.origin;
								}}
							>
								End Session
							</Button>
						</Typography>
					</Alert>
				</Grid>
			) : null}
			<Typography variant="h4">Chosen Club Components</Typography>
			<Grid item xs={12} display="flex">
				<Box sx={{ paddingRight: 2, width: showBallSelection ? "33.33%" : "50%" }}>
					<Typography variant="overline" display="block">
						Shaft Selected
					</Typography>
					<Autocomplete
						value={clubState.selectedShaft}
						forcePopupIcon={false}
						options={clubState.clubInfo.shafts.data}
						getOptionLabel={(option) => option.name}
						renderInput={(params) => (
							<TextField {...params} sx={{ textOverflow: "ellipsis" }} />
						)}
						onChange={(event, val) => setSelectedShaft(val)}
					/>
				</Box>
				<Box sx={{ paddingRight: 2, width: showBallSelection ? "33.33%" : "50%" }}>
					<Typography variant="overline" display="block">
						Head Selected
					</Typography>
					<Autocomplete
						value={clubState.selectedHead}
						forcePopupIcon={false}
						options={clubState.clubInfo.heads.data}
						getOptionLabel={(option) => option.name}
						renderInput={(params) => (
							<TextField {...params} sx={{ textOverflow: "ellipsis" }} />
						)}
						onChange={(event, val) => setSelectedHead(val)}
					/>
				</Box>
				{showBallSelection && (
					<Box sx={{ paddingRight: 2, width: "33.33%" }}>
						<Typography variant="overline" display="block">
							Ball Selected
						</Typography>
						<Autocomplete
							value={clubState.selectedBall}
							forcePopupIcon={false}
							options={clubState.ballOptions || []}
							getOptionLabel={(option) => option.name || "No ball selected"}
							renderInput={(params) => (
								<TextField {...params} sx={{ textOverflow: "ellipsis" }} />
							)}
							onChange={(event, val) => setSelectedBall(val)}
						/>
					</Box>
				)}
			</Grid>
			<Grid
				item
				xs={12}
				display={submitError ? "none" : "flex"}
				flexDirection="row-reverse"
				sx={{ paddingRight: 2, width: "50%", marginTop: 2, gap: 2 }}
			>
				<Tooltip title="Complete fitting session for this player" arrow>
					<span>
						<Button
							variant="contained"
							color="error"
							onClick={async () => {
								const success = await submitFitting(
									clubState.selectedShaft.name,
									clubState.selectedHead.name,
									clubState.selectedBall?.name || null
								);
								if (success) {
									localStorage.removeItem("sessionId");
									localStorage.removeItem("fitterName");
									localStorage.removeItem("locationId");
									localStorage.removeItem("rightHanded");
									localStorage.removeItem("gender");
									localStorage.removeItem("clubType");
									localStorage.removeItem("handicap");
									localStorage.removeItem("sessionData");
									localStorage.removeItem("fittingStartedSessionId");
									localStorage.removeItem("ballFittingCompleted");
									window.location = window.location.origin;
								} else {
									setSubmitError(true);
								}
							}}
							disabled={
								clubState.selectedHead && clubState.selectedShaft ? false : true
							}
						>
							End Session
						</Button>
					</span>
				</Tooltip>
				<Tooltip title="Fit another club type for this player" arrow>
					<span>
						<Button
							variant="contained"
							color="primary"
							onClick={async () => {
								const success = await submitFitting(
									clubState.selectedShaft.name,
									clubState.selectedHead.name,
									clubState.selectedBall?.name || null
								);
								if (success) {
									// Show success toast (before clearing localStorage)
									setShowSuccessToast(true);

									// Wait a bit before clearing and navigating
									setTimeout(() => {
										// Clear session-specific data (keep user preferences)
										localStorage.removeItem("clubType");
										localStorage.removeItem("sessionData");
										localStorage.removeItem("fittingStartedSessionId");
										localStorage.removeItem("ballFittingCompleted");
										// Generate new session ID
										localStorage.setItem("sessionId", uuidv4());
										// Set flag to auto-open modal and auto-navigate back
										localStorage.setItem("continueSession", "true");
										// Clear selected clubs for new session
										setSelectedShaft(undefined);
										setSelectedHead(undefined);
										setSelectedBall(undefined);
										// Navigate
										navigate("/");
									}, 1200);
								} else {
									setSubmitError(true);
								}
							}}
							disabled={
								clubState.selectedHead && clubState.selectedShaft ? false : true
							}
						>
							Next Club
						</Button>
					</span>
				</Tooltip>
			</Grid>
			<Snackbar
				open={showSuccessToast}
				autoHideDuration={1200}
				onClose={() => setShowSuccessToast(false)}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
						backgroundColor: "#4caf50",
						color: "white",
						padding: "16px 24px",
						borderRadius: "8px",
						boxShadow: 3,
					}}
				>
					<CheckCircleIcon sx={{ fontSize: 40 }} />
					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						{clubType}
					</Typography>
				</Box>
			</Snackbar>
		</Grid>
	);
};

export default EndSession;
